import React, { useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import * as Accordion from "ds/Accordion";
import { CloseButton } from "ds/Button";
import * as Sticker from "ds/Sticker";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  loadOrganizationEstimate,
  organizationEstimateSelector,
  organizationLoadingSelector
} from "Reducers/organization";
import { getPrepaymentTransactions } from "Reducers/organization/prepayment";
import * as S from "src/organization/pages/billing/details/containers/Billing/Billing.styles";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import type { Organization, OrganizationPrepayment } from "@packages/client";

export const Prepayment = ({
  organization,
  prepayment
}: {
  organization: Organization;
  prepayment: OrganizationPrepayment;
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [hasClosedPrepaymentAlert, setHasClosedPrepaymentAlert] =
    useState(false);

  const transactions = useAppSelector(
    state => state.organizationPrepayment.transactions[organization.id]
  );

  const organizationEstimate = useSelectorWithUrlParams(
    organizationEstimateSelector
  );

  const organizationLoading = useAppSelector(organizationLoadingSelector);

  useEffect(() => {
    dispatch(getPrepaymentTransactions({ organizationId: organization.id }));
  }, [dispatch, organization.id]);

  const isEstimateLoadedOrLoading = organizationEstimate || organizationLoading;

  useEffect(() => {
    if (!isEstimateLoadedOrLoading) {
      dispatch(loadOrganizationEstimate(organization));
    }
  }, [dispatch, organization, isEstimateLoadedOrLoading]);

  return (
    <Accordion.Item value="prepayment_transaction_history">
      <Accordion.SectionHeader>
        {intl.formatMessage({
          id: "billing.details.prepaid_transaction_history"
        })}
      </Accordion.SectionHeader>
      <Accordion.Content>
        {!hasClosedPrepaymentAlert &&
          (prepayment.balance.amount === 0 ? (
            <Sticker.Root priority="information">
              <Sticker.Icon priority="information" />
              <Sticker.Text>
                {intl.formatMessage({
                  id: "billing.details.prepaid_funds_depleted_message"
                })}
              </Sticker.Text>
              <CloseButton
                onClick={() => {
                  setHasClosedPrepaymentAlert(true);
                }}
              />
            </Sticker.Root>
          ) : (
            (prepayment.balance.amount ?? 0) <
              (organizationEstimate?.current.total.amount ?? 0) && (
              <Sticker.Root priority="normal">
                <Sticker.Icon priority="normal" />
                <Sticker.Text>
                  {intl.formatMessage({
                    id: "billing.details.projected_costs_exceed_prepaid_message"
                  })}
                </Sticker.Text>
                <CloseButton
                  onClick={() => {
                    setHasClosedPrepaymentAlert(true);
                  }}
                />
              </Sticker.Root>
            )
          ))}

        <S.PrepaymentSummaryWrapper>
          <div>
            <Typography.BodyText weight="semiBold">
              {(prepayment.balance.amount ?? 0) > 0 ? "+" : ""}
              {prepayment.balance.formatted}
            </Typography.BodyText>
            <Typography.BodyText
              $color="granite"
              weight="semiBold"
              size="extraSmall"
            >
              {intl.formatMessage({
                id: "billing.details.current_funds_remaining"
              })}
            </Typography.BodyText>
          </div>
          <div>
            <Typography.BodyText
              $color={
                (prepayment.balance.amount ?? 0) <
                (organizationEstimate?.current.total.amount ?? 0)
                  ? "granite"
                  : "ebony"
              }
              weight="semiBold"
            >
              {organizationEstimate?.current.total.formatted}
            </Typography.BodyText>
            <Typography.BodyText
              $color="granite"
              weight="semiBold"
              size="extraSmall"
            >
              {intl.formatMessage({
                id: "billing.details.projected_total_this_month"
              })}
            </Typography.BodyText>
          </div>
        </S.PrepaymentSummaryWrapper>

        <S.PrepaidFundsMessage>
          {intl.formatMessage({
            id: "billing.details.prepaid_funds_message"
          })}
        </S.PrepaidFundsMessage>
        <S.TransactionsTable>
          <Typography.BodyText weight="semiBold" size="extraSmall">
            {intl.formatMessage({ id: "billing.details.date" })}
          </Typography.BodyText>
          <Typography.BodyText weight="semiBold" size="extraSmall">
            {intl.formatMessage({
              id: "billing.details.description"
            })}
          </Typography.BodyText>
          <Typography.BodyText weight="semiBold" size="extraSmall">
            {intl.formatMessage({ id: "billing.details.order_id" })}
          </Typography.BodyText>
          <Typography.BodyText weight="semiBold" size="extraSmall">
            {intl.formatMessage({ id: "billing.details.amount" })}
          </Typography.BodyText>

          {transactions?.map(transaction => (
            <React.Fragment key={transaction.order_id}>
              <Typography.BodyText weight="semiBold" size="extraSmall">
                {momentjs(transaction.created).format("MMM D, YYYY")}
              </Typography.BodyText>
              <S.TransactionsTableDescription>
                {transaction.message}
              </S.TransactionsTableDescription>
              <Typography.BodyText size="small">
                <Link
                  to={`/${organization.name}/-/billing/history/${transaction.order_id}`}
                >
                  {transaction.order_id}
                </Link>
              </Typography.BodyText>
              <Typography.BodyText weight="semiBold" size="small">
                {transaction.amount.amount > 0 ? "+" : ""}
                {transaction.amount.formatted}
              </Typography.BodyText>
            </React.Fragment>
          ))}
        </S.TransactionsTable>
      </Accordion.Content>
    </Accordion.Item>
  );
};
